import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { AuthActions } from "src/redux/Auth/actions";
import { ModalActions } from "src/redux/Modal/actions";
import { validatePassword, PasswordAndVerify } from "../SignUp";
import Login from "../";
import Button from "src/components/common/Button";
import Logo from "src/asset/logo-ksl-white.png";
import styles from "./styles.module.scss";
import { SpinnerActions } from "src/redux/Spinner/actions";

// Step 1 — ask the gateway to email a reset link. (The old UI emailed a 6-digit
// code the user typed here; we email a link that returns to the ResetForm below
// with the token pre-filled, so there's no code to type.)
export default (props) => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState(props.email || "");
    const [error, setError] = useState(null);

    const requestLink = (e) => {
        e.preventDefault();
        dispatch(SpinnerActions.Open());
        dispatch(AuthActions.ForgotPassword(email))
            .then(() => {
                dispatch(
                    ModalActions.Open(RequestSent, {
                        ignoreClickOutside: true,
                        width: 440,
                        customProps: { email },
                    })
                );
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => dispatch(SpinnerActions.Close()));
    };

    return (
        <div className={styles.forgot}>
            <h1>Forgot your password?</h1>
            <p className={styles.tagline}>
                We'll email you a link to reset it.
            </p>

            {error && (
                <div className={styles.error} data-testid="error-span">
                    {error}
                </div>
            )}

            <form className={styles.row}>
                <div className={styles.left}>
                    <label className={styles.emailLabel}>
                        Work email address
                        <input
                            className={styles.email}
                            placeholder="name@company.com"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="email-input"
                        />
                    </label>
                </div>
                <div className={styles.right}>
                    <Button
                        value="Send Link"
                        dataTestId="request-code-button"
                        onClick={requestLink}
                    />
                </div>
            </form>
        </div>
    );
};

// Shown after requesting a link — the user continues from their inbox. Uniform
// wording whether or not the account exists (the gateway never reveals it).
const RequestSent = ({ email }) => (
    <div className={styles.confirm}>
        <h1>Check your email</h1>
        <div className={styles.splitter} />
        <h2>Reset link sent.</h2>
        <p>
            If an account exists for <b>{email}</b>, we've emailed a link to reset
            your password. The link expires in one hour. If you don't see it,
            please check your spam folder.
        </p>
    </div>
);

// Step 2 — reached from the emailed link (token + email carried in the URL).
// This is the old "Reset Password" prompt with the verification-code field
// replaced by the auto-filled token, so the user only chooses a new password.
export const ResetForm = ({ email, token }) => {
    const dispatch = useDispatch();
    const [password, setPassword] = useState("");
    const [verify, setVerify] = useState("");
    const [error, setError] = useState(null);
    const [disabled, setDisabled] = useState(true);

    useEffect(() => {
        const passwordError = validatePassword(password, verify);
        if (passwordError.length > 0) {
            setError(passwordError);
            setDisabled(true);
            return;
        }
        setError(null);
        setDisabled(false);
    }, [password, verify]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        dispatch(SpinnerActions.Open());
        dispatch(AuthActions.VerifyForgotPassword(email, token, password))
            .then(() => {
                dispatch(ModalActions.Open(Confirmed, { wide: true, padding0: true }));
                dispatch(SpinnerActions.Close());
            })
            .catch((err) => {
                dispatch(SpinnerActions.Close());
                setError(err.message);
            });
    };

    return (
        <form className={styles.confirm}>
            <h1>Reset Password</h1>
            <div className={styles.splitter} />
            <h2>Choose a new password</h2>
            <p>
                Setting a new password for <b>{email}</b>.
            </p>

            <PasswordAndVerify
                password={password}
                setPassword={setPassword}
                verify={verify}
                setVerify={setVerify}
            />

            {error && (
                <div className={styles.error} data-testid="error-span">
                    {error}
                </div>
            )}

            <Button
                value="Reset Password"
                onClick={handleSubmit}
                disabled={disabled}
                dataTestId="reset-password-button"
            />
        </form>
    );
};

const Confirmed = () => {
    const dispatch = useDispatch();

    return (
        <div className={styles.verified}>
            <img className={styles.logo} alt="KSL" src={Logo} width="85" height="26" />

            <div className={styles.row}>
                <div className={styles.left}>
                    <p>Your password has been reset.</p>
                    <p>You can now start using TRU.</p>
                </div>
                <div className={styles.right}>
                    <Button
                        value="Continue TRU"
                        onClick={() =>
                            dispatch(ModalActions.Open(Login, { small: true }))
                        }
                        data-testid="continue-button"
                    />
                </div>
            </div>
        </div>
    );
};
